import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../data/apiFetch";
import { fileDispute, cancelRequest } from "../data/requests";
import { submitReview } from "../data/reviews";
import { toId } from "../lib/subjects";
import { getRequestStatusLabel } from "../lib/requestStatus";
import { toast } from "./components/ToastHost";

const brand = { bg: "#272c39" };

const Badge = ({ tone = "neutral", children }) => {
  const cls =
    tone === "good"
      ? "bg-[#98dea3]/15 text-[#98dea3] ring-[#98dea3]/20"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200 ring-amber-400/20"
        : tone === "danger"
          ? "bg-red-500/15 text-red-200 ring-red-400/20"
          : "bg-white/10 text-white/80 ring-white/10";

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs ring-1 ${cls}`}>
      {children}
    </span>
  );
};

function Card({ text }) {
  return (
    <div className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 text-white/80">
      {text}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
      <div className="text-xs text-white/60">{label}</div>
      <div className="mt-1 text-sm text-white/90">{value || "—"}</div>
    </div>
  );
}

export default function RequestDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const auth = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "null");
    } catch {
      return null;
    }
  }, []);

  const role = (auth?.role || "student").toLowerCase();
  const isTutor = role === "tutor";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [reqData, setReqData] = useState(null); // { request, student, tutor }
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState(false);

  const fmt = useCallback(
    (iso) =>
      new Date(iso).toLocaleString(i18n.language || undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [i18n.language]
  );

  // Try specific subject, then field/sphere group, then the raw value — a
  // general request carries a sphere key that lives under subjectGroups.*.
  const locSubject = useCallback((s) => {
    const id = toId(s);
    const subjKey = `subjects.${id}`;
    const subjVal = t(subjKey);
    if (subjVal !== subjKey) return subjVal;
    const groupKey = `subjectGroups.${id}`;
    const groupVal = t(groupKey);
    if (groupVal !== groupKey) return groupVal;
    return s;
  }, [t]);

  const load = useCallback(async () => {
    if (!auth?.token) return;
    setLoading(true);
    setErr("");

    try {
      const data = await apiFetch(`/api/requests/${encodeURIComponent(id)}`);
      setReqData(data);

      // wallet optional (only tutor really needs it)
      try {
        const w = await apiFetch(`/api/me/wallet`);
        setWallet(w);
      } catch {
        setWallet(null);
      }
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to load request");
    } finally {
      setLoading(false);
    }
  }, [auth?.token, id]);

  useEffect(() => {
    load();
  }, [load]);

  const r = reqData?.request;

  const status = String(r?.status || "NEW").toUpperCase();
  const kind = String(r?.kind || "TRIAL").toUpperCase();

  const isClosed = status === "CLOSED";
  const isChatActive = status === "CHAT_ACTIVE";
  const isUnlocked = !!r?.contactsUnlocked || isChatActive;

  // Derived "consultation held" — while the chat is active, infer that the
  // consultation has taken place so we can surface a "Консультацію проведено"
  // badge (and open the review form) without waiting for the specialist to press
  // the outcome button manually. Basis:
  //   • preferredAt — the scheduled time has passed; or
  //   • unlockedAt  — no time was set, so assume it happened once the chat has
  //                   been open for at least 24h.
  // Once the specialist marks an outcome (TRIAL_DONE / BECAME_CLIENT / CLOSED)
  // the status label already conveys this, so the badge is limited to CHAT_ACTIVE.
  const consultationHeld = useMemo(() => {
    if (status !== "CHAT_ACTIVE") return false;
    const now = Date.now();
    if (r?.preferredAt && now > new Date(r.preferredAt).getTime()) return true;
    if (r?.unlockedAt && now - new Date(r.unlockedAt).getTime() > 24 * 60 * 60 * 1000) return true;
    return false;
  }, [r?.preferredAt, r?.unlockedAt, status]);

  // Client has left (or the system auto-added) a review for this request.
  const isRated = !!r?.review;

  const pricePts = useMemo(() => {
    const n = Number(r?.priceCredits);
    return Number.isFinite(n) ? n : 0;
  }, [r?.priceCredits]);

  // Chat peer
  const peerId = useMemo(() => {
    if (!r) return null;
    return isTutor ? r.studentId : r.tutorId;
  }, [r, isTutor]);

  const peerName = useMemo(() => {
    if (!r) return "";
    return isTutor
      ? r.studentName || t("common.student")
      : r.tutorName || t("common.tutor");
  }, [r, isTutor, t]);

  const openChat = useCallback(() => {
    if (!peerId) return;
    navigate(`/chat/${peerId}?name=${encodeURIComponent(peerName)}`);
  }, [navigate, peerId, peerName]);

  // Dispute state
  const [disputeFiled, setDisputeFiled] = useState(false);
  const [disputeResult, setDisputeResult] = useState(null);

  // Review state (student side)
  const [reviewSent, setReviewSent] = useState(false);
  const [reviewPositive, setReviewPositive] = useState(null); // true | false | null
  const [reviewComment, setReviewComment] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);

  // Cancel state (student only, NEW status only)
  const [cancelBusy, setCancelBusy] = useState(false);

  const handleCancel = useCallback(async () => {
    if (!r) return;
    setCancelBusy(true);
    try {
      await cancelRequest(r.id);
      toast(`✅ ${t("request.details.cancelBtn")}`);
      await load();
    } catch (e) {
      toast(`❌ ${e?.message || "Failed"}`);
    } finally {
      setCancelBusy(false);
    }
  }, [r, t, load]);

  const unlockAndOpenChat = useCallback(async () => {
    if (!r) return;
    setBusy(true);
    try {
      await apiFetch(`/api/requests/${r.id}/respond`, { method: "POST" });
      toast(`✅ ${t("dashboardtutor.toast.unlocked")}`);
      await load();
      openChat();
    } catch (e) {
      if (e?.status === 402 || e?.data?.error === "INSUFFICIENT_POINTS") {
        toast(`⚠️ ${t("dashboardtutor.toast.needTopup")}`);
      } else {
        toast(`❌ ${e?.message || "Failed"}`);
      }
      console.error(e);
    } finally {
      setBusy(false);
    }
  }, [r, t, load, openChat]);

  const setOutcome = useCallback(
    async (nextStatus) => {
      if (!r) return;
      setBusy(true);
      try {
        await apiFetch(`/api/requests/${r.id}/outcome`, {
          method: "POST",
          body: JSON.stringify({ status: nextStatus }),
        });
        toast(`✅ ${t("common.saved")}`);
        await load();
      } catch (e) {
        console.error(e);
        toast(`❌ ${e?.message || "Failed"}`);
      } finally {
        setBusy(false);
      }
    },
    [r, t, load]
  );

  const submitDispute = useCallback(
    async () => {
      if (!r) return;
      setBusy(true);
      try {
        const res = await fileDispute(r.id);
        if (res?.alreadyFiled) {
          toast(`ℹ️ Dispute already filed`);
          setDisputeFiled(true);
          setDisputeResult(res.dispute);
        } else {
          setDisputeFiled(true);
          setDisputeResult(res);
          if (res?.refunded) {
            toast(`✅ Refund approved automatically — ${res.feeAmount} pts returned`);
          } else {
            toast(`📋 Dispute filed — admin will review within 24h`);
          }
          await load();
        }
      } catch (e) {
        toast(`❌ ${e?.message || "Failed to file dispute"}`);
        console.error(e);
      } finally {
        setBusy(false);
      }
    },
    [r, load]
  );

  const sendReview = useCallback(async () => {
    if (!r || reviewPositive === null) return;
    setReviewBusy(true);
    try {
      await submitReview({
        requestId: r.id,
        stars: reviewPositive ? 5 : 2,
        comment: reviewComment.trim() || null,
      });
      setReviewSent(true);
      toast(`✅ Відгук надіслано`);
    } catch (e) {
      if (e?.data?.error === "ALREADY_REVIEWED") {
        setReviewSent(true);
      } else if (e?.data?.error === "REVIEW_WINDOW_CLOSED") {
        toast(`⏳ Вікно для відгуку закрито (72 години минули)`);
      } else {
        toast(`❌ ${e?.message || "Помилка"}`);
      }
    } finally {
      setReviewBusy(false);
    }
  }, [r, reviewPositive, reviewComment]);

  // Compute review window status for student
  const reviewWindowOpen = useMemo(() => {
    if (!r?.reviewWindowOpenedAt) return false;
    return Date.now() - new Date(r.reviewWindowOpenedAt).getTime() < 72 * 60 * 60 * 1000;
  }, [r?.reviewWindowOpenedAt]);

  const statusTone =
    status === "NEW"
      ? "warn"
      : status === "CHAT_ACTIVE" || status === "TRIAL_DONE"
        ? "good"
        : status === "BECAME_CLIENT"
          ? "good"
          : status === "CANCELLED_BY_STUDENT"
            ? "danger"
            : "neutral";

  const statusLabel = useMemo(() => getRequestStatusLabel(t, status), [t, status]);

  const kindLabel = useMemo(() => {
    if (r?.packageTitle) {
      return t("packages.requestLabel", {
        title: r.packageTitle,
        price: Number(r.packagePrice || 0).toLocaleString("uk-UA"),
      });
    }
    if (r?.isFreeIntro) return t("request.kind.INTRO");
    if (kind === "TRIAL") {
      const d = Number(r?.durationMin);
      return Number.isFinite(d) && d > 0
        ? t("request.consultDuration", { min: d })
        : t("request.kind.TRIAL");
    }
    if (kind === "REGULAR") return t("request.kind.REGULAR");
    return kind;
  }, [kind, r?.isFreeIntro, r?.durationMin, r?.packageTitle, r?.packagePrice, t]);

  const modeLabel = useMemo(() => {
    const raw = String(r?.mode || "").toUpperCase();
    if (raw === "ONLINE") return t("request.mode.ONLINE");
    if (raw === "OFFLINE") return t("request.mode.OFFLINE");
    if (raw === "BOTH") return t("request.mode.BOTH");
    return r?.mode || "—";
  }, [r?.mode, t]);

  const stageText = useMemo(() => {
    // Client-facing wording differs from the specialist's: a client can't
    // "unlock/open" a chat — they wait for the specialist to accept. Showing
    // the specialist-oriented "Open chat to get contact" text to clients was
    // confusing (the chat is locked for them at this stage).
    if (status === "NEW") return isTutor ? t("request.details.stageNew") : t("request.details.stageNewStudent");
    if (status === "CHAT_ACTIVE") {
      if (consultationHeld)
        return isTutor
          ? t("request.details.stageConsultationHeldTutor")
          : t("request.details.stageConsultationHeldStudent");
      return isTutor ? t("request.details.stageChatActive") : t("request.details.stageChatActiveStudent");
    }
    if (status === "TRIAL_DONE") return t("request.details.stageTrialDone");
    if (status === "BECAME_CLIENT") return t("request.details.stageWon");
    if (status === "CLOSED") return t("request.details.stageClosed");
    if (status === "CANCELLED_BY_STUDENT") return t("request.details.stageCancelledByStudent");
    return t("request.details.stageFallback");
  }, [status, t, isTutor, consultationHeld]);

  return (
    <main className="min-h-screen" style={{ background: brand.bg, color: "white" }}>
      <div className="mx-auto max-w-5xl px-4 pt-10 pb-24">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-unbounded text-3xl font-extrabold tracking-tight">
              {t("request.details.title")}
            </h1>
            <p className="mt-1 text-white/65 text-sm">
              {isTutor
                ? t("request.details.subtitleTutor")
                : t("request.details.subtitleStudent")}
            </p>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm hover:bg-white/10"
          >
            ← {t("common.back")}
          </button>
        </div>

        {loading ? (
          <Card text={t("common.loading")} />
        ) : err ? (
          <Card text={err} />
        ) : !r ? (
          <Card text={t("common.notFound")} />
        ) : (
          <section className="mt-6 grid gap-6 lg:grid-cols-3">
            {/* LEFT */}
            <div className="lg:col-span-2 space-y-6">
              {/* Main */}
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-lg font-semibold">
                      {isTutor
                        ? `${r.studentName || t("common.student")}`
                        : `${r.tutorName || t("common.tutor")}`}
                      <span className="text-white/60"> • </span>
                      {locSubject(r.subject)}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <Badge>{kindLabel}</Badge>
                      <Badge tone={statusTone}>{statusLabel}</Badge>
                      {consultationHeld ? (
                        <Badge tone="good">{t("request.details.consultationHeld")}</Badge>
                      ) : null}
                      {isRated ? (
                        <Badge tone="good">{t("request.details.rated")}</Badge>
                      ) : null}
                      {r.level ? <Badge>{t(`levels.${toId(r.level)}`) || r.level}</Badge> : null}
                    </div>
                  </div>

                  {/* Fee only for tutors when NOT unlocked */}
                  {isTutor && !isUnlocked ? (
                    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-3">
                      <div className="text-xs text-white/60">{t("dashboardtutor.fee")}</div>
                      <div className="mt-1 text-xl font-extrabold">
                        {pricePts} {t("wallet.points")}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Meta */}
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <Meta label={t("profile.format")} value={modeLabel} />
                  <Meta
                    label={t("common.time")}
                    value={r.preferredAt ? fmt(r.preferredAt) : "—"}
                  />
                </div>

                <div className="mt-4 rounded-2xl bg-[#7269e3]/10 ring-1 ring-[#7269e3]/30 p-4 text-sm text-white/90">
                  {stageText}
                </div>

                {/* Video call link — appears once the consultation is confirmed */}
                {r.meetingUrl ? (
                  <a
                    href={r.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-[#98dea3] px-4 py-3 text-sm font-bold text-[#141826] hover:brightness-105"
                  >
                    🎥 {t("request.details.joinCall", { defaultValue: "Приєднатись до дзвінка" })}
                  </a>
                ) : null}

                {/* Message */}
                <div className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5">
                  <div className="text-xs text-white/60">
                    {t("profile.messageOptional")}
                  </div>
                  <div className="mt-2 whitespace-pre-wrap text-sm text-white/85">
                    {r.message || t("request.details.noMessage")}
                  </div>
                </div>
              </div>

              {/* Contacts */}
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="font-unbounded text-lg font-semibold">
                    {t("request.details.contacts")}
                  </h2>

                  {isTutor ? (
                    !isUnlocked ? (
                      <Badge tone="warn">{t("request.details.locked")}</Badge>
                    ) : (
                      <Badge tone="good">{t("request.details.unlocked")}</Badge>
                    )
                  ) : null}
                </div>

                {isUnlocked ? (
                  <div className="mt-4 rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm text-white/90 font-medium">
                        {isTutor
                          ? (reqData?.student?.name || r.studentName || t("common.student"))
                          : (reqData?.tutor?.name || r.tutorName || t("common.tutor"))}
                      </div>
                      <div className="mt-0.5 text-xs text-white/50">
                        {t("request.details.chatOnly")}
                      </div>
                    </div>
                    <button
                      onClick={openChat}
                      className="shrink-0 rounded-xl bg-[#7269e3] px-4 py-2 text-sm font-semibold hover:brightness-110"
                    >
                      {t("actions.openChat")}
                    </button>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-white/70">
                    {t("request.details.contactsLockedHint")}
                  </p>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className="space-y-6">
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
                <h2 className="font-unbounded text-lg font-semibold">
                  {t("request.details.actions")}
                </h2>

                {isTutor ? (
                  <div className="mt-3 text-white/70 text-sm">
                    {t("dashboardtutor.balance.short")}{" "}
                    <span className="text-white font-semibold">{wallet?.balance ?? 0}</span>
                  </div>
                ) : null}

                <div className="mt-4 space-y-2">
                  {/* Chat CTA */}
                  {isTutor && !isUnlocked ? (
                    <>
                      <button
                        disabled={busy || isClosed}
                        onClick={unlockAndOpenChat}
                        className="w-full rounded-xl bg-[#98dea3]/20 px-4 py-2 text-sm font-semibold text-[#98dea3] hover:bg-[#98dea3]/25 disabled:opacity-60"
                      >
                        {t("request.details.unlockAndChat")} • {pricePts} {t("wallet.points")}
                      </button>

                      <p className="text-xs text-white/60">
                        {t("request.details.unlockHint")}
                      </p>
                    </>
                  ) : isUnlocked ? (
                    <button
                      disabled={busy}
                      onClick={openChat}
                      className="w-full rounded-xl bg-[#7269e3] px-4 py-2 text-sm font-semibold hover:brightness-110 disabled:opacity-60"
                    >
                      {t("actions.openChat")}
                    </button>
                  ) : (
                    /* Student side, request not yet accepted: chat stays
                       locked until the specialist unlocks the request. The
                       backend enforces this too (403 CHAT_LOCKED). */
                    <div className="w-full rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3 text-sm text-white/60">
                      {t("request.details.chatAfterAccept", {
                        defaultValue: "Чат стане доступним після того, як спеціаліст прийме ваш запит.",
                      })}
                    </div>
                  )}

                  {/* Outcome — only when unlocked */}
                  {isTutor && isUnlocked ? (
                    <div className="mt-4 rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                      <div className="text-xs text-white/60">
                        {t("request.details.outcome")}
                      </div>
                      <p className="mt-2 text-xs text-white/60">
                        {t("request.details.outcomeHint")}
                      </p>

                      <div className="mt-3 flex flex-col gap-2">
                        {kind === "TRIAL" ? (
                          <button
                            disabled={busy || isClosed}
                            onClick={() => setOutcome("TRIAL_DONE")}
                            className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-60"
                          >
                            {t("request.details.trialDone")}
                          </button>
                        ) : null}

                        <button
                          disabled={busy || isClosed}
                          onClick={() => setOutcome("BECAME_CLIENT")}
                          className="rounded-xl bg-[#98dea3]/20 px-4 py-2 text-sm font-semibold text-[#98dea3] hover:bg-[#98dea3]/25 disabled:opacity-60"
                        >
                          {t("request.details.becameClient")}
                        </button>

                        <button
                          disabled={busy || isClosed}
                          onClick={() => setOutcome("CLOSED")}
                          className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-60"
                        >
                          {t("request.details.close")}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {/* Dispute / Refund request — only when unlocked and not already a client */}
                  {isTutor && isUnlocked && status !== "BECAME_CLIENT" ? (
                    <div className="mt-4 rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                      <div className="text-xs text-white/60 font-semibold mb-2">
                        Поскаржитися / Запит на повернення
                      </div>

                      {disputeFiled || r?.dispute ? (
                        <div className="rounded-xl bg-white/5 p-3 text-sm">
                          {disputeResult?.refunded || r?.dispute?.status === "AUTO_APPROVED" || r?.dispute?.status === "APPROVED" ? (
                            <div className="text-[#98dea3]">✅ Кошти повернуто</div>
                          ) : disputeResult?.autoDecision === "AUTO_REJECTED" || r?.dispute?.status === "REJECTED" ? (
                            <div className="text-red-300">❌ У поверненні відмовлено — була активна комунікація</div>
                          ) : (
                            <div className="text-yellow-300">⏳ Заявку подано — адмін розгляне протягом 24 год</div>
                          )}
                        </div>
                      ) : (
                        <>
                          <p className="text-xs text-white/55 mb-3">
                            Клієнт не відповідав або зник? Подайте заявку — система автоматично перевірить активність у чаті та повернуть кошти або направить до адміна.
                          </p>
                          <button
                            disabled={busy || isClosed}
                            onClick={submitDispute}
                            className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-60"
                          >
                            {busy ? "Відправляємо…" : "Подати заявку на повернення"}
                          </button>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Review prompt — student only. Shows once the specialist opened
                  the 72h window OR the consultation is inferred held (by time). */}
              {!isTutor && (reviewWindowOpen || consultationHeld) && !r?.review && (
                <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
                  <div className="text-sm font-semibold text-white/90 mb-3">
                    Як пройшла консультація?
                  </div>

                  {reviewSent ? (
                    <div className="rounded-xl bg-[#98dea3]/10 p-3 text-sm text-[#98dea3]">
                      ✅ Дякуємо за відгук!
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => setReviewPositive(true)}
                          className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition ${
                            reviewPositive === true
                              ? "border-[#98dea3]/50 bg-[#98dea3]/20 text-[#98dea3]"
                              : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
                          }`}
                        >
                          👍 Все добре
                        </button>
                        <button
                          onClick={() => setReviewPositive(false)}
                          className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition ${
                            reviewPositive === false
                              ? "border-red-400/40 bg-red-500/15 text-red-300"
                              : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
                          }`}
                        >
                          👎 Є зауваження
                        </button>
                      </div>

                      {reviewPositive !== null && (
                        <>
                          <textarea
                            rows={2}
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            placeholder="Коментар (необов'язково)…"
                            maxLength={1000}
                            className="w-full resize-none rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7269e3] mb-2"
                          />
                          <button
                            disabled={reviewBusy}
                            onClick={sendReview}
                            className="w-full rounded-xl bg-[#7269e3] py-2 text-sm font-semibold hover:brightness-110 disabled:opacity-60"
                          >
                            {reviewBusy ? "Надсилаємо…" : "Надіслати відгук"}
                          </button>
                        </>
                      )}

                      <p className="mt-2 text-xs text-white/35">
                        Вікно відкрито 72 години. Після — система автоматично додасть позитивний відгук.
                      </p>
                    </>
                  )}
                </div>
              )}

              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6">
                <div className="text-sm text-white/80 font-medium">
                  {t("request.details.tipTitle")}
                </div>
                <p className="mt-2 text-sm text-white/65">
                  {isTutor
                    ? t("request.details.tipTextTutor")
                    : t("request.details.tipTextStudent")}
                </p>
              </div>

              {/* Cancel — student only, only before specialist responds */}
              {!isTutor && status === "NEW" && (
                <div className="rounded-2xl border border-red-500/15 bg-red-500/5 ring-1 ring-red-500/10 p-5">
                  <button
                    disabled={cancelBusy}
                    onClick={handleCancel}
                    className="w-full rounded-xl border border-red-500/25 bg-transparent px-4 py-2 text-sm text-red-300/80 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-60 transition"
                  >
                    {cancelBusy ? t("request.details.cancelling") : t("request.details.cancelBtn")}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
